import { useState, useEffect } from 'react';
import { useTheme, themes } from './ThemeProvider';
import { Button } from './ui/button';
import { Footer } from './Footer';
import { AtootbandanLogo } from './AtootbandanLogo';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { API_URL, BACKEND_URL, SOCKET_URL } from '../lib/api-client';
import { toast } from 'sonner';
import { SEOWrapper } from './SEOWrapper';
import { Skeleton } from './ui/skeleton';
import { ProfileCard } from './ProfileCard';
import DesktopMatchesLayout from './DesktopMatchesLayout';
import ViewAllProfilesPage from './ViewAllProfilesPage';
import { 
  Sparkles, 
  Crown, 
  Globe, 
  Moon, 
  Gift, 
  HelpCircle, 
  Shield, 
  ArrowRight, 
  Users, 
  CheckCircle, 
  MessageCircle, 
  Quote, 
  Star, 
  Server,
  Heart,
  MapPin,
  Briefcase,
  GraduationCap,
  Bookmark,
  Eye
} from 'lucide-react';

interface HomePageProps {
  onNavigate: (screen: 'home' | 'login' | 'register' | 'app' | 'admin' | 'blogs' | 'community' | 'refer-earn' | 'search' | 'help' | 'premium-marketplace' | 'kundli-maker') => void;
  isLoggedIn?: boolean;
  userRole?: 'user' | 'marketing' | 'admin';
}

export default function HomePage({ onNavigate, isLoggedIn, userRole }: HomePageProps) {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const [showServerDialog, setShowServerDialog] = useState(false);
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [serverInfo, setServerInfo] = useState<any>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [liveProfiles, setLiveProfiles] = useState<any[]>([]);
  const [liveTotalCount, setLiveTotalCount] = useState<number>(85);

  // Fetch real live profiles for More Matches on Home page
  useEffect(() => {
    const loadLiveHomeProfiles = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers: any = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_URL}/profiles/discovery?limit=8`, { headers });
        if (response.ok) {
          const data = await response.json();
          const items = data.data?.profiles || data.profiles || [];
          const total = data.data?.pagination?.total || data.pagination?.total || items.length;
          if (items && items.length > 0) {
            setLiveProfiles(items);
          }
          if (total > 0) {
            setLiveTotalCount(total);
          }
        }
      } catch (err) {
        // Fallback to initial defaults
      }
    };

    loadLiveHomeProfiles();
  }, []);

  // Simulate loading for featured profiles
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingProfiles(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // SMS Invite Auto-Trigger on Home Page Load (only for logged in users in native app)
  useEffect(() => {
    const triggerSmsInviteIfNeeded = async () => {
      // Only run in native app and if user is logged in
      if (!(window as any).isNativeApp || !isLoggedIn) {
        return;
      }

      try {
        // Get user from localStorage
        const userStr = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        
        if (!userStr || !token) {
          return;
        }

        const user = JSON.parse(userStr);
        
        // Check if user has already sent SMS
        if (user.smsSent === true) {
          return;
        }

        // Fetch settings to check if auto-trigger is enabled
        const settingsResponse = await fetch(`${API_URL}/settings/app`);
        
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          const smsInviteEnabled = settingsData?.settings?.features?.smsInviteEnabled;
          const smsInviteAutoTrigger = settingsData?.settings?.features?.smsInviteAutoTrigger;
          
          // Only show if both SMS invite and auto-trigger are enabled
          if (smsInviteEnabled !== true || smsInviteAutoTrigger !== true) {
            return;
          }

          // Trigger SMS modal via React Native WebView
          if ((window as any).ReactNativeWebView) {
            const message = {
              type: 'AUTO_SHOW_SMS_MODAL',
              referralCode: user.referralCode || 'ATOOTBANDAN',
              userName: user.fullName || user.firstName || 'Your Friend',
              smsSent: user.smsSent || false
            };
            
            (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
          }
        }
      } catch (error) {
        // Silent fail
      }
    };

    // Delay slightly to ensure everything is loaded
    const timeoutId = setTimeout(triggerSmsInviteIfNeeded, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [isLoggedIn]);

  const checkServerStatus = async () => {
    setServerStatus('checking');
    try {
      const response = await fetch(`${API_URL}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServerInfo(data);
        setServerStatus('online');
        toast.success('Server is online and running!');
      } else {
        setServerStatus('offline');
        toast.error('Server responded with error');
      }
    } catch (error) {
      setServerStatus('offline');
      toast.error('Cannot connect to server');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <SEOWrapper
      title="Atootbandan - Find Your Perfect Life Partner | India's Most Trusted Matrimonial Site"
      description="Discover your soulmate on Atootbandan, India's most trusted matrimonial platform. Join millions of verified profiles to find the perfect match. 100% verified, AI-powered matching, instant chat."
      keywords="matrimony, marriage, shaadi, bride, groom, wedding, life partner, matchmaking, Indian matrimony, verified profiles, Atootbandan"
      breadcrumbs={[
        { name: 'Home', url: window.location.origin }
      ]}
    >
      <div className={`min-h-screen ${currentTheme.light} flex flex-col`}>
        {/* Header */}
        <header className="border-b bg-white/80 border-gray-200 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="cursor-pointer" onClick={() => onNavigate('home')}>
              <AtootbandanLogo size="sm" showText={true} />
            </div>
            <div className="hidden md:flex items-center gap-4">
              {/* HIDDEN: AI Search */}
              <Button variant="ghost" onClick={() => onNavigate('search')} className="hidden">
                <Sparkles className="size-4 mr-2" />
                AI Search
              </Button>
              {/* HIDDEN: Premium */}
              <Button variant="ghost" onClick={() => onNavigate('premium-marketplace')} className="hidden">
                <Crown className="size-4 mr-2" />
                Premium
              </Button>
              <Button variant="ghost" onClick={() => onNavigate('blogs')}>
                Blog
              </Button>
              {/* HIDDEN: Community */}
              <Button variant="ghost" onClick={() => onNavigate('community')} className="hidden">
                <Globe className="size-4 mr-2" />
                Community
              </Button>
              <Button variant="ghost" onClick={() => onNavigate('kundli-maker')}>
                <Moon className="size-4 mr-2" />
                Kundli
              </Button>
              <Button variant="ghost" onClick={() => onNavigate('refer-earn')}>
                <Gift className="size-4 mr-2" />
                Refer & Earn
              </Button>
              <Button variant="ghost" onClick={() => onNavigate('help')}>
                <HelpCircle className="size-4 mr-2" />
                Help
              </Button>
              {/* HIDDEN: Admin Panel */}
              {/* {userRole === 'admin' && (
                  <Button variant="ghost" onClick={() => onNavigate('admin')} className="text-purple-600 hover:text-purple-700">
                    <Shield className="size-4 mr-2" />
                    Admin Panel
                  </Button>
                )} */}
            </div>
            <div className="flex items-center gap-3">
              {isLoggedIn ? (
                <Button onClick={() => onNavigate('app')} className={`${currentTheme.gradient} text-white ${currentTheme.primaryHover}`}>
                  Go to App
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => onNavigate('login')}>
                    Login
                  </Button>
                  <Button onClick={() => onNavigate('register')} className={`${currentTheme.gradient} text-white ${currentTheme.primaryHover}`}>
                    Register Free
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 bg-gray-50">
          {!isLoggedIn ? (
            <>
              {/* Hero Section */}
              <section className="relative py-20 bg-gradient-to-r from-rose-600 via-pink-600 to-purple-700 text-white overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]" />
                <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full mb-6 text-sm font-medium">
                    <Sparkles className="size-4 text-amber-300" />
                    <span>India's Most Trusted Matrimonial Platform</span>
                  </div>
                  <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight max-w-4xl mx-auto">
                    Find Your Perfect Life Partner & Begin Your Beautiful Journey
                  </h1>
                  <p className="text-xl text-rose-100 max-w-2xl mx-auto mb-10">
                    Join thousands of verified brides & grooms. 100% privacy control, intelligent matchmaking, and genuine profiles.
                  </p>

                  {/* Hero Quick Search Box */}
                  <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-4xl mx-auto text-gray-800 border border-rose-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 text-left">Looking For</label>
                        <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500">
                          <option value="female">Woman (Bride)</option>
                          <option value="male">Man (Groom)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 text-left">Age</label>
                        <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500">
                          <option>21 to 28 yrs</option>
                          <option>25 to 32 yrs</option>
                          <option>28 to 35 yrs</option>
                          <option>32 to 40 yrs</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 text-left">Religion</label>
                        <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500">
                          <option>Hindu</option>
                          <option>Muslim</option>
                          <option>Christian</option>
                          <option>Sikh</option>
                          <option>Jain</option>
                        </select>
                      </div>
                      <div>
                        <Button 
                          onClick={() => onNavigate('register')}
                          className="w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white font-bold py-3.5 rounded-xl text-base shadow-lg transition-all"
                        >
                          Search Matches
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-rose-100">
                    <span className="flex items-center gap-1.5"><CheckCircle className="size-4 text-emerald-300" /> 100% Verified Profiles</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="size-4 text-emerald-300" /> Strict Privacy</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="size-4 text-emerald-300" /> Free Registration</span>
                  </div>
                </div>
              </section>

              {/* Featured Profiles Section */}
              <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">Featured Profiles</h2>
                      <p className="text-gray-600 mt-1">Discover recently active profiles looking for a life partner</p>
                    </div>
                    <Button onClick={() => onNavigate('register')} variant="outline" className="border-rose-500 text-rose-600 hover:bg-rose-50">
                      View All Profiles <ArrowRight className="size-4 ml-1" />
                    </Button>
                  </div>

                  {/* Grid of sample cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {liveProfiles.slice(0, 8).map((profile, idx) => (
                      <div key={profile._id || idx} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all">
                        <div className="h-64 relative overflow-hidden bg-gray-100">
                          <img 
                            src={profile.photos?.[0]?.url || profile.profilePhoto || ''} 
                            alt={profile.firstName || 'Profile'}
                            className="w-full h-full object-cover" 
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute bottom-3 left-3 text-white">
                            <h3 className="font-bold text-lg">{profile.firstName || 'Verified User'}, {profile.age || 26}</h3>
                            <p className="text-xs text-rose-200">{profile.city || 'India'} • {profile.religion || 'Hindu'}</p>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-xs text-gray-600 mb-3 line-clamp-1">{profile.occupation || profile.education || 'Professional'}</p>
                          <Button onClick={() => onNavigate('register')} className="w-full bg-rose-500 hover:bg-rose-600 text-white size-sm rounded-xl">
                            Connect Now
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </>
          ) : (
            <div className="max-w-7xl mx-auto px-4 py-4">
              <DesktopMatchesLayout
                onViewProfile={(profile) => {
                  onNavigate('app');
                }}
              />
            </div>
          )}
          <section className="py-16 bg-gradient-to-br from-orange-50 via-rose-50 to-purple-50">
            <div className="max-w-7xl mx-auto px-4">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Left Side - Content */}
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-rose-100 px-4 py-2 rounded-full mb-6 w-fit">
                      <Moon className="size-5 text-orange-600" />
                      <span className="font-semibold text-orange-600">Free Kundli Maker</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent">
                      Get Your Vedic Birth Chart
                    </h2>
                    <p className="text-gray-600 mb-6 text-lg">
                      Discover your destiny with a detailed Kundli featuring planetary positions, houses, nakshatras, and dosha analysis - completely free!
                    </p>
                    <ul className="space-y-3 mb-8">
                      <li className="flex items-center gap-3">
                        <div className="size-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="size-4 text-orange-600" />
                        </div>
                        <span className="text-gray-700">Complete planetary positions & houses</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="size-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="size-4 text-orange-600" />
                        </div>
                        <span className="text-gray-700">Nakshatra & birth star details</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="size-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="size-4 text-orange-600" />
                        </div>
                        <span className="text-gray-700">Manglik & Kaal Sarp dosha analysis</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="size-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="size-4 text-orange-600" />
                        </div>
                        <span className="text-gray-700">Downloadable & shareable</span>
                      </li>
                    </ul>
                    <Button 
                      onClick={() => onNavigate('kundli-maker')}
                      className="bg-gradient-to-r from-orange-600 to-rose-600 hover:from-orange-700 hover:to-rose-700 text-white text-lg py-6 px-8 w-fit"
                    >
                      <Moon className="size-5 mr-2" />
                      Create Your Kundli Now
                    </Button>
                  </div>

                  {/* Right Side - Visual */}
                  <div className="bg-gradient-to-br from-orange-400 via-rose-500 to-purple-600 p-8 md:p-12 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-10 left-10 size-32 border-4 border-white rounded-full" />
                      <div className="absolute bottom-10 right-10 size-40 border-4 border-white rounded-full" />
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 size-64 border-4 border-white rounded-full" />
                    </div>
                    <div className="relative z-10 text-center text-white">
                      <div className="size-48 mx-auto mb-6 relative">
                        <div className="absolute inset-0 bg-white rounded-full opacity-20 animate-pulse" />
                        <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center">
                          <Star className="size-24 text-orange-600" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold mb-2">Ancient Wisdom</h3>
                      <p className="text-white/90">Modern Technology</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Success Stories */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-3xl font-bold text-center mb-12">Happy Success Stories</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-gray-50 rounded-2xl p-8 relative">
                    <Quote className="absolute top-6 left-6 size-8 text-rose-200 fill-current" />
                    <p className="text-gray-600 mb-6 relative z-10 pt-6">
                      "We met on Atootbandan 6 months ago and it was instant connection. The platform is so easy to use and genuine."
                    </p>
                    <div className="flex items-center gap-4">
                      <img 
                        src={`https://images.unsplash.com/photo-${i === 1 ? '1529626455594-4ff0802cfb7e' : i === 2 ? '1517841905240-472988babdf9' : '1539571696357-5a69c17a67c6'}?w=100&h=100&fit=crop`}
                        alt="Couple"
                        className="size-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold">Rohan & Sneha</div>
                        <div className="text-xs text-gray-500">Married Jan 2025</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Refer and Earn */}
          <section className={`py-16 ${currentTheme.gradient} text-white`}>
            <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-12">
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
                  <Gift className="size-5" />
                  <span className="font-medium">Referral Program</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Refer Friends & Earn Big!</h2>
                <p className="text-white/90 text-lg mb-8 max-w-xl">
                  Invite your friends to Atootbandan and earn 50% commission on their premium subscription. It's a win-win!
                </p>
                <Button size="lg" className="bg-white text-rose-600 hover:bg-gray-50 font-semibold">
                  Start Referring
                </Button>
              </div>
              <div className="flex-1 flex justify-center">
                 <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 max-w-sm w-full">
                    <div className="flex items-center justify-between mb-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold">₹500</div>
                        <div className="text-xs text-white/70">Per Referral</div>
                      </div>
                      <div className="h-10 w-px bg-white/20"></div>
                      <div className="text-center">
                        <div className="text-3xl font-bold">Unltd</div>
                        <div className="text-xs text-white/70">Earning Potential</div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm">
                        <div className="size-6 rounded-full bg-white/20 flex items-center justify-center text-xs">1</div>
                        <span>Share your unique link</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <div className="size-6 rounded-full bg-white/20 flex items-center justify-center text-xs">2</div>
                        <span>Friend subscribes to Premium</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <div className="size-6 rounded-full bg-white/20 flex items-center justify-center text-xs">3</div>
                        <span>You get instant payout</span>
                      </div>
                    </div>
                 </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <Footer onAdminAccess={() => onNavigate('admin')} />
        
        {/* Server Status Dialog */}
        <Dialog open={showServerDialog} onOpenChange={setShowServerDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Server className="size-5" />
                Server Configuration
              </DialogTitle>
              <DialogDescription>
                Check API endpoint and server connectivity
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* API URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium">API URL</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm">
                    {API_URL}
                  </code>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyToClipboard(API_URL)}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              {/* Backend URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Backend URL</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm">
                    {BACKEND_URL}
                  </code>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyToClipboard(BACKEND_URL)}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              {/* Socket URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Socket URL</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm">
                    {SOCKET_URL}
                  </code>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyToClipboard(SOCKET_URL)}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              {/* Server Status */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Server Status</label>
                <div className="flex items-center justify-between p-3 rounded border">
                  <div className="flex items-center gap-2">
                    <div className={`size-3 rounded-full ${
                      serverStatus === 'online' ? 'bg-green-500 animate-pulse' :
                      serverStatus === 'offline' ? 'bg-red-500' :
                      'bg-yellow-500 animate-pulse'
                    }`} />
                    <span className="text-sm font-medium">
                      {serverStatus === 'online' ? 'Online' :
                       serverStatus === 'offline' ? 'Offline' :
                       'Checking...'}
                    </span>
                  </div>
                  <Button 
                    size="sm"
                    onClick={checkServerStatus}
                    disabled={serverStatus === 'checking'}
                  >
                    {serverStatus === 'checking' ? 'Checking...' : 'Test Connection'}
                  </Button>
                </div>
              </div>

              {/* Server Info (if available) */}
              {serverInfo && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Server Response</label>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-32">
                    {JSON.stringify(serverInfo, null, 2)}
                  </pre>
                </div>
              )}

              {/* Environment Info */}
              <div className="pt-4 border-t">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500">Environment</div>
                    <div className="font-medium">
                      {typeof import.meta !== 'undefined' && import.meta.env?.DEV ? 'Development' : 'Production'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Mode</div>
                    <div className="font-medium">
                      {typeof import.meta !== 'undefined' && import.meta.env?.MODE ? import.meta.env.MODE : 'production'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Floating Server Status Button - Debug Mode Only */}
        {typeof import.meta !== 'undefined' && import.meta.env?.DEV && (
          <button
            onClick={() => setShowServerDialog(true)}
            className="fixed bottom-4 left-4 p-3 bg-gray-800 hover:bg-gray-700 text-white rounded-full shadow-lg transition-all hover:scale-110 z-50"
            title="Server Configuration"
          >
            <Server className="size-5" />
          </button>
        )}
        
        {/* Floating Admin Button (Optional, kept discrete) - HIDDEN per request */}
        {/* <button
          onClick={() => onNavigate('admin')}
          className="fixed bottom-4 right-4 opacity-50 hover:opacity-100 transition-opacity p-2 bg-gray-800 text-white rounded-full text-xs z-50"
        >
          Admin
        </button> */}
      </div>
    </SEOWrapper>
  );
}